/**
 * Utilitários de cálculo e formatação para o DRE.
 */
import { FINANCIAL_TYPES } from "../../../../constants/financial";

/**
 * Formata valor em BRL (Real brasileiro).
 */
export const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

/**
 * Formata variação percentual.
 */
export const formatPercent = (value) => {
    const num = Number(value) || 0;
    return `${num >= 0 ? "+" : ""}${num.toFixed(1)}%`;
};

/**
 * Calcula variação percentual entre dois valores.
 */
export const calcVariation = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / Math.abs(previous)) * 100;
};

/**
 * Agrupa financialRecords por categoria para montar o DRE.
 *
 * @param {Array} records - Registros financeiros do período
 * @param {Array} categories - Categorias financeiras (plano de contas)
 * @param {string} dateField - Campo de data para filtragem ("competenceDate")
 * @returns {Object} { income: [...], expense: [...], totals: {...} }
 */
export const buildDREData = (records, categories, dateField = "competenceDate") => {
    // Agrupar categorias por tipo
    const incomeCategories = categories.filter(c => c.type === FINANCIAL_TYPES.INCOME).sort((a, b) => (a.order || 0) - (b.order || 0));
    const expenseCategories = categories.filter(c => c.type === FINANCIAL_TYPES.EXPENSE).sort((a, b) => (a.order || 0) - (b.order || 0));

    // Calcular totais por categoria
    const categoryTotals = {};

    for (const record of records) {
        const catId = record.idCategory || "uncategorized";
        if (!categoryTotals[catId]) {
            categoryTotals[catId] = {
                grossAmount: 0,
                netAmount: 0,
                feeAmount: 0,
                count: 0,
            };
        }
        categoryTotals[catId].grossAmount += Number(record.grossAmount) || 0;
        categoryTotals[catId].netAmount += Number(record.netAmount) || 0;
        categoryTotals[catId].feeAmount += Number(record.feeAmount) || 0;
        categoryTotals[catId].count += 1;
    }

    // Montar linhas do DRE para RECEITAS
    const incomeLines = incomeCategories.map(cat => ({
        id: cat.id,
        name: cat.name,
        order: cat.order,
        isSystem: cat.isSystem,
        grossAmount: categoryTotals[cat.id]?.grossAmount || 0,
        netAmount: categoryTotals[cat.id]?.netAmount || 0,
        feeAmount: categoryTotals[cat.id]?.feeAmount || 0,
        count: categoryTotals[cat.id]?.count || 0,
    })).filter(line => line.grossAmount > 0 || line.isSystem);

    // Montar linhas do DRE para DESPESAS
    const expenseLines = expenseCategories.map(cat => ({
        id: cat.id,
        name: cat.name,
        order: cat.order,
        isSystem: cat.isSystem,
        grossAmount: categoryTotals[cat.id]?.grossAmount || 0,
        netAmount: categoryTotals[cat.id]?.netAmount || 0,
        feeAmount: categoryTotals[cat.id]?.feeAmount || 0,
        count: categoryTotals[cat.id]?.count || 0,
    })).filter(line => line.grossAmount > 0 || line.isSystem);

    // Totais gerais
    const totalIncome = incomeLines.reduce((sum, l) => sum + l.grossAmount, 0);
    const totalIncomeNet = incomeLines.reduce((sum, l) => sum + l.netAmount, 0);
    const totalIncomeFees = incomeLines.reduce((sum, l) => sum + l.feeAmount, 0);
    const totalExpense = expenseLines.reduce((sum, l) => sum + l.grossAmount, 0);
    const totalExpenseNet = expenseLines.reduce((sum, l) => sum + l.netAmount, 0);

    return {
        income: incomeLines,
        expense: expenseLines,
        totals: {
            totalIncome,
            totalIncomeNet,
            totalIncomeFees,
            totalExpense,
            totalExpenseNet,
            netResult: totalIncomeNet - totalExpenseNet,
            grossResult: totalIncome - totalExpense,
            margin: totalIncome > 0 ? ((totalIncomeNet - totalExpenseNet) / totalIncome * 100) : 0,
        }
    };
};

/**
 * Filtra registros por período mensal.
 * @param {Array} records - Registros financeiros
 * @param {number} year - Ano
 * @param {number} month - Mês (1-12)
 * @returns {Array} Registros filtrados
 */
export const filterByMonth = (records, year, month) => {
    const prefix = `${year}-${String(month).padStart(2, "0")}`;
    return records.filter(r => {
        const date = r.competenceDate || r.dueDate || "";
        return date.startsWith(prefix);
    });
};

/**
 * Filtra registros por trimestre.
 * @param {Array} records
 * @param {number} year
 * @param {number} quarter - 1, 2, 3 ou 4
 * @returns {Array}
 */
export const filterByQuarter = (records, year, quarter) => {
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    return records.filter(r => {
        const date = r.competenceDate || r.dueDate || "";
        if (!date.startsWith(`${year}-`)) return false;
        const month = parseInt(date.split("-")[1]);
        return month >= startMonth && month <= endMonth;
    });
};
